import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task, type TaskInstance } from 'ember-concurrency';
import type {
  OtaHotelInfo,
  ParityStrategyBreakdown,
  ParityStrategyEvolution,
  ParityStrategyHotel,
  ParityStrategyIssue,
  ParityStrategyPromotion,
  ParityStrategySubscription,
} from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  DIMENSIONS,
  HISTORICAL_POINTS_AMOUNT,
  LATEST,
} from 'lumen/utils/constants/pi-constant-values';
import { getFormattedLastPossibleExtractDate } from 'lumen/utils/parity-analytics/date-utils';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type ParityStrategyRouteQueryParams = {
  activeFilters: string[];
  extractDate: string;
  historicalExtractDate: string;
};

type ParityStrategyModel = {
  breakdowns: TaskInstance<{
    breakdowns: ParityStrategyBreakdown[];
    hotels: ParityStrategyHotel[];
    meta: {
      number_of_hotels_with_issues: number;
    };
    promotions: ParityStrategyPromotion[];
    subscriptions: ParityStrategySubscription[];
  }>;
  evolutions: TaskInstance<ParityStrategyEvolution[]>;
  issues?: TaskInstance<ParityStrategyIssue[]>;
};

export default class MarketParityStrategyRoute extends Route {
  queryParams = {
    extractDate: { refreshModel: true },
    historicalExtractDate: { refreshModel: true },
    activeFilters: { refreshModel: true, type: 'array' },
  };

  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @tracked cachedActiveFilters: string[] = [];
  @tracked cachedExtractDate: string | null = null;
  @tracked cachedHistoricalExtractDate: string | null = null;

  modelObject = trackedObject({
    breakdowns: {
      isError: false,
      isRunning: false,
    },
    evolutions: {
      isError: false,
      isRunning: false,
      value: [] as ParityStrategyEvolution[] | null,
    },
    issues: {
      isError: false,
      isRunning: false,
    },
  });

  fetchBreakdownDataTask = task(
    { restartable: true },
    async ({ marketId, filters, formattedExtractDateWithFallback }) => {
      try {
        // Await subscriptions request before performing any other requests
        const subscriptions = await this.fetchSubscriptionsTask.perform({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          marketId,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          filters,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          formattedExtractDateWithFallback,
        });

        const allBreakdowns = await this.store.query<ParityStrategyBreakdown>(
          'parity-strategy/breakdown',
          {
            namespace: APIGATEWAY,
            typeKey: 'paritystrategy/breakdowns',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            market_id: marketId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            extract_date: formattedExtractDateWithFallback,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filters,
          },
        );

        const breakdowns: ParityStrategyBreakdown[] = [];
        const hotels: ParityStrategyHotel[] = [];
        const promotions: ParityStrategyPromotion[] = [];

        allBreakdowns.forEach((breakdown) => {
          switch (breakdown.modelName) {
            case 'parity-strategy/breakdown': {
              breakdowns.push(breakdown);
              break;
            }

            case 'parity-strategy/hotel': {
              hotels.push(breakdown as unknown as ParityStrategyHotel);
              break;
            }

            case 'parity-strategy/promotion': {
              promotions.push(breakdown as unknown as ParityStrategyPromotion);
              break;
            }

            default: {
              // Do nothing
            }
          }
        });

        return {
          breakdowns,
          hotels,
          promotions,
          subscriptions,
          meta: allBreakdowns.meta as unknown as Record<string, unknown>,
        };
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchEvolutionsTask = task(
    { restartable: true },
    async ({
      marketId,
      extractDateMoment,
      isLatestExtractDateSelected,
      historicalExtractDateMoment,
      isLatestHistoricalExtractDateSelected,
      filters,
    }) => {
      const extractDates = [];
      const lastPossibleExtractDate = getFormattedLastPossibleExtractDate();

      // First check the extract dates to display in the evolution chart
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      let rangeEnd = historicalExtractDateMoment;
      if (isLatestHistoricalExtractDateSelected) {
        rangeEnd = this.hotelAndMarketService.toLocalTime(
          lastPossibleExtractDate,
        );
        extractDates.push(LATEST);
      }
      // First extract date should be just outside of range for the reference value (vs. last week)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const rangeStart = rangeEnd.subtract(HISTORICAL_POINTS_AMOUNT, 'week');

      //  If the extractDate (from the gauge) is not visible in the evolution chart, fetch it separately
      if (
        isLatestExtractDateSelected &&
        !isLatestHistoricalExtractDateSelected
      ) {
        extractDates.push(LATEST);
      } else if (
        !isLatestExtractDateSelected &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        !extractDateMoment.isBetween(rangeStart, rangeEnd)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        extractDates.push(extractDateMoment.format('YYYY-MM-DD'));
      }

      // Same for the week before the extractDate (for reference value)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const weekBeforeExtractDateMoment = isLatestExtractDateSelected
        ? this.hotelAndMarketService.toLocalTime(lastPossibleExtractDate)
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          extractDateMoment.subtract(1, 'week');
      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        !weekBeforeExtractDateMoment.isBetween(
          rangeStart,
          rangeEnd,
          'day',
          '[]',
        )
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        extractDates.push(weekBeforeExtractDateMoment.format('YYYY-MM-DD'));
      }

      try {
        const evolutions = await this.store.query<ParityStrategyEvolution>(
          'parity-strategy/evolution',
          {
            typeKey: 'paritystrategy/evolutions',
            namespace: APIGATEWAY,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            market_id: marketId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            extract_date_range_start: rangeStart.format('YYYY-MM-DD'),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            extract_date_range_end: rangeEnd.format('YYYY-MM-DD'),
            extract_dates: extractDates,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filters,
          },
        );
        return [...evolutions];
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchIssuesTask = task(
    { restartable: true },
    async ({
      marketId,
      filters,
      formattedExtractDateWithFallback,
      subscriptionId,
    }) => {
      try {
        // OtaHotelInfos have to be fully loaded before fetching issues
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        await this.fetchOtaHotelInfosTask.perform({ subscriptionId });

        const issues = await this.store.query<ParityStrategyIssue>(
          'parity-strategy/issue',
          {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            subscriptionId, // Not passed as a QP, but used to construct the endpoint path in the adapter
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            market_id: marketId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            extract_date: formattedExtractDateWithFallback,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filters,
          },
        );

        return [...issues];
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchOtaHotelInfosTask = task(
    { restartable: true },
    async ({ subscriptionId }) => {
      try {
        const requestParams = {
          namespace: APIGATEWAY,
          typeKey: 'usermanagement/otahotelinfos',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          subscription_id: subscriptionId,
        };

        return await this.store.query<OtaHotelInfo>(
          'ota-hotel-info',
          requestParams,
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchSubscriptionsTask = task(
    { restartable: true },
    async ({ marketId, filters, formattedExtractDateWithFallback }) => {
      try {
        const subscriptions =
          await this.store.query<ParityStrategySubscription>(
            'parity-strategy/subscription',
            {
              namespace: APIGATEWAY,
              typeKey: 'paritystrategy/subscriptions',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              market_id: marketId,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              extract_date: formattedExtractDateWithFallback,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              filters,
            },
          );
        return [...subscriptions];
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/require-await
  async beforeModel(transition: Transition): Promise<void> {
    if (
      !this.access.hasParityInsightAccess ||
      !this.access.hasParityStrategyAccess
    ) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateParityStrategyQueryParams(
        transition,
        this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
      );
    }
  }

  deactivate(): void {
    this.cachedExtractDate = null;
    this.cachedHistoricalExtractDate = null;
    this.cachedActiveFilters = [];

    this.modelObject = trackedObject({
      breakdowns: {
        isError: false,
        isRunning: false,
      },
      evolutions: {
        isError: false,
        isRunning: false,
        value: [] as ParityStrategyEvolution[] | null,
      },
      issues: {
        isError: false,
        isRunning: false,
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/require-await
  async model(params: ParityStrategyRouteQueryParams) {
    const isExtractDateUpdated = params.extractDate !== this.cachedExtractDate;
    const isHistoricalExtractDateUpdated =
      params.historicalExtractDate !== this.cachedHistoricalExtractDate;
    const isActiveFiltersUpdated =
      JSON.stringify(params.activeFilters) !==
      JSON.stringify(this.cachedActiveFilters);

    this.cachedExtractDate = params.extractDate;
    this.cachedHistoricalExtractDate = params.historicalExtractDate;
    this.cachedActiveFilters = [...params.activeFilters];

    const lastPossibleExtractDate = getFormattedLastPossibleExtractDate();
    const extractDateMoment = this.hotelAndMarketService.toLocalTime(
      params.extractDate,
    );
    const historicalExtractDateMoment = this.hotelAndMarketService.toLocalTime(
      params.historicalExtractDate,
    );

    const isLatestExtractDateSelected = extractDateMoment.isAfter(
      lastPossibleExtractDate,
      'day',
    );
    const isLatestHistoricalExtractDateSelected =
      historicalExtractDateMoment.isAfter(lastPossibleExtractDate, 'day');

    const formattedExtractDateWithFallback = isLatestExtractDateSelected
      ? LATEST
      : extractDateMoment.format('YYYY-MM-DD');

    const latestExtractDate = isLatestExtractDateSelected
      ? LATEST
      : params.extractDate;

    const isExtractDateCached = this.modelObject.evolutions.value?.some(
      (evolution: ParityStrategyEvolution) => {
        return evolution.extractDate === latestExtractDate;
      },
    );

    if (
      !isExtractDateCached ||
      isHistoricalExtractDateUpdated ||
      isActiveFiltersUpdated
    ) {
      this.modelObject.evolutions = this.fetchEvolutionsTask.perform({
        marketId: this.hotelAndMarketService.market.id,
        extractDateMoment,
        isLatestExtractDateSelected,
        historicalExtractDateMoment,
        isLatestHistoricalExtractDateSelected,
        filters: params.activeFilters,
      });
    }

    if (isExtractDateUpdated || isActiveFiltersUpdated) {
      this.modelObject.breakdowns = this.fetchBreakdownDataTask.perform({
        marketId: this.hotelAndMarketService.market.id,
        formattedExtractDateWithFallback,
        filters: params.activeFilters,
      });
    }

    const hotelFilter = params.activeFilters.find((filter) =>
      filter.startsWith(DIMENSIONS.HOTEL),
    );
    if (hotelFilter) {
      this.modelObject.issues = this.fetchIssuesTask.perform({
        subscriptionId: hotelFilter.split('.')[1],
        marketId: this.hotelAndMarketService.market.id,
        formattedExtractDateWithFallback,
        filters: params.activeFilters.filter(
          (filter) => !filter.startsWith(DIMENSIONS.HOTEL),
        ),
      });
    }

    return this.modelObject as ParityStrategyModel;
  }
}
